import type { ReactNode, RefObject } from "react";
import { createContext, useContext, useEffect, useRef } from "react";
import { ControlLabel } from "./Field";

/**
 * 下拉面板的收起时机：点外面收起、外部滚动收起。
 *
 * 两处细节都被踩过，所以收进这里，别再各写一遍：
 *
 * 1. **滚动监听必须用捕获阶段**。抽屉/表格容器内部的滚动不冒泡到 document，
 *    不捕获就会留下一个飘在原地、指错行的面板（面板是 fixed，按触发器实测坐标定位的）。
 * 2. **但面板自己的滚动不算**。捕获阶段会连面板内那个 `overflow-y-auto` 列表的滚动
 *    一起收到，于是「用滚轮翻列表」变成「一滚就关」——地区有 280 条，等于翻不动。
 *    触发器没动，面板位置还是对的，这种滚动必须放行。
 */
export function useDismissOnOutside(
  open: boolean,
  refs: { box: RefObject<HTMLElement | null>; panel: RefObject<HTMLElement | null> },
  onDismiss: () => void,
) {
  useEffect(() => {
    if (!open) return;
    const inside = (n: EventTarget | null) =>
      n instanceof Node && (refs.box.current?.contains(n) || refs.panel.current?.contains(n));
    const onDown = (e: MouseEvent) => { if (!inside(e.target)) onDismiss(); };
    const onScroll = (e: Event) => { if (!refs.panel.current?.contains(e.target as Node)) onDismiss(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onDismiss);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onDismiss);
    };
  }, [open, refs.box, refs.panel, onDismiss]);
}

const OverlayDepth = createContext(0);
const openOverlays = new Map<symbol, { depth: number; order: number }>();
let overlayOrder = 0;
let unlockedOverflow = "";

/** Esc only closes the topmost layer. Nested locale drawers must not discard
 * their parent editor, and body scrolling stays locked until every layer closes. */
export function useOverlay(onClose?: () => void, open = true) {
  const depth = useContext(OverlayDepth);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const id = Symbol("overlay");
    if (openOverlays.size === 0) unlockedOverflow = document.body.style.overflow;
    openOverlays.set(id, { depth, order: ++overlayOrder });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const top = [...openOverlays].sort((a, b) => b[1].depth - a[1].depth || b[1].order - a[1].order)[0];
      if (top?.[0] !== id) return;
      e.preventDefault();
      close.current?.();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      openOverlays.delete(id);
      if (openOverlays.size === 0) document.body.style.overflow = unlockedOverflow;
    };
  }, [depth, open]);
  return depth;
}

/**
 * `dismissible = false`：**点背景和按 Esc 都不关**，只留 × 与底部按钮。
 *
 * 给装着表单的弹窗用。默认仍是 true，既有调用点行为不变——
 * 只读 / 确认类弹窗随手点掉是方便，装着半篇正文的弹窗随手点掉是事故，
 * 而且没有任何挽回余地（没有草稿、没有撤销）。
 */
export function Modal({
  title, children, footer, onClose, width = 520, dismissible = true,
}: {
  title: ReactNode; children: ReactNode; footer?: ReactNode;
  onClose?: () => void; width?: number; dismissible?: boolean;
}) {
  const depth = useOverlay(dismissible ? onClose : undefined);
  return (
    <OverlayDepth.Provider value={depth + 1}>
    <div
      onMouseDown={(e) => { if (dismissible && e.target === e.currentTarget) onClose?.(); }}
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/30 px-5 py-[6vh]"
      data-collab-root
    >
      <div style={{ width }} className="flex max-h-[88vh] max-w-[96vw] flex-col rounded-base bg-white shadow-light">
        <header className="flex items-center justify-between border-b border-line-lighter px-5 py-4">
          <div className="text-base font-semibold text-ink">{title}</div>
          <button type="button" onClick={onClose} className="text-lg leading-none text-ink-secondary hover:text-ink">×</button>
        </header>
        {/* 断开 ControlLabel：弹层是从某个表格格子 / 字段里打开的，不断开的话里面每个输入框都会顶着那一格的名字 */}
        <div className="flex-1 overflow-auto p-5 text-ink-regular"><ControlLabel.Provider value={undefined}>{children}</ControlLabel.Provider></div>
        {footer && <footer className="flex justify-end gap-2.5 border-t border-line-lighter px-5 py-3">{footer}</footer>}
      </div>
    </div>
    </OverlayDepth.Provider>
  );
}

/** `dismissible = false` 的含义同 {@link Modal}：装着输入的抽屉不该被随手点掉 */
export function Drawer({
  title, children, onClose, width = 880, extra, footer, dismissible = true,
}: {
  title: ReactNode; children: ReactNode; onClose?: () => void;
  width?: number; extra?: ReactNode; footer?: ReactNode; dismissible?: boolean;
}) {
  const depth = useOverlay(dismissible ? onClose : undefined);
  return (
    <OverlayDepth.Provider value={depth + 1}>
    <div
      onMouseDown={(e) => { if (dismissible && e.target === e.currentTarget) onClose?.(); }}
      className="fixed inset-0 z-[55] flex justify-end bg-black/30"
      data-collab-root
    >
      <div style={{ width }} className="flex h-full max-w-[94vw] flex-col bg-white">
        <header className="flex items-center justify-between gap-3 border-b border-line-lighter px-5 py-4">
          <div className="text-base font-semibold text-ink">{title}</div>
          <div className="flex items-center gap-2.5">
            {extra}
            <button type="button" onClick={onClose} className="text-lg leading-none text-ink-secondary hover:text-ink">×</button>
          </div>
        </header>
        <div className="flex-1 overflow-auto p-5"><ControlLabel.Provider value={undefined}>{children}</ControlLabel.Provider></div>
        {footer && <footer className="flex shrink-0 items-center gap-2.5 border-t border-line-lighter bg-white px-5 py-3">{footer}</footer>}
      </div>
    </div>
    </OverlayDepth.Provider>
  );
}
